import angular, { IQService } from "angular";

import { vsName as processModuleName } from "../../../Process";
import {
  getAuthorizedUiElementConfigForProcessGroup,
  ProcessGroupConfig,
} from "../../../Process/config";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import {
  SelectContractModalService,
  vsServiceName as SelectContractModalServiceName,
} from "../../../Contract/shared/components/selectContractModal";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { IRowCommandEventArgs } from "../../../records/grids/controller";
import { vsModuleName as contractSharedModuleName } from "../../../Contract/shared";

import {
  ProcessInformationService,
  ProcessInformationServiceProvider,
  vsProviderName as ProcessInformationServiceProviderName,
  vsServiceName as ProcessInformationServiceName,
} from "../../../Process/services/processInformationService";
import {
  desertDiamondContractProcessRouteConfig,
  desertDiamondMachineProcessRouteConfig,
} from "./routes";

import { vsObjectName as contractObjectName } from "../../../Contract/shared/configs/contractInstance";
import { Record } from "../../../records/server/crud";
import {
  ContractServiceProvider,
  vsProviderName as ContractServiceProviderName,
  IContractTab,
  infoState as contractInfoState,
} from "../../../Contract/shared/ContractService";
import { Contract } from "../../../Contract/shared/model/contract";
import { ProcessInformation } from "../../../Process/model/ProcessGroups";
import { Ng1StateDeclaration, StateService } from "@uirouter/angularjs";
import { AuthorizedUiElementConfig } from "../../../ui/authorizedUiElement";
import { machineObjectName } from "../../shared/names";
import {
  SelectRecordModalService,
  vsServiceName as SelectRecordModalServiceName,
} from "../../../records/services/selectRecordModal";

export const vsModuleName = "Slotcheck.DesertDiamond";

angular
  .module(vsModuleName, [processModuleName, contractSharedModuleName])
  .config(desertDiamondContractProcessRouteConfig)
  .config(desertDiamondMachineProcessRouteConfig)
  .config(configureContractProcess)
  .config(configureMachineProcess);

configureMachineProcess.$inject = [
  ProcessInformationServiceProviderName,
  ContractServiceProviderName,
];
function configureMachineProcess(
  processInformationServiceProvider: ProcessInformationServiceProvider,
) {
  const processDetailTemplate =
    require("!html-loader!./machineProcessDetail.html").default;

  const machineProcessGroupConfig: ProcessGroupConfig =
    getMachineProcessGroupConfig(processDetailTemplate);

  processInformationServiceProvider.addRuntimeConfig(machineProcessGroupConfig);
}

configureContractProcess.$inject = [
  ProcessInformationServiceProviderName,
  ContractServiceProviderName,
];
function configureContractProcess(
  processInformationServiceProvider: ProcessInformationServiceProvider,
  contractServiceProvider: ContractServiceProvider,
) {
  const processDetailTemplate =
    require("!html-loader!./contractProcessDetail.html").default;

  const contractProcessGroupConfig: ProcessGroupConfig =
    getContractProcessGroupConfig(processDetailTemplate);

  processInformationServiceProvider.addRuntimeConfig(
    contractProcessGroupConfig,
  );

  const detailUiElement = getAuthorizedUiElementConfigForProcessGroup(
    contractProcessGroupConfig,
    contractInfoState,
    ["contract", (contract) => getDefaultQueryForContractInstance(contract)],
    onNewContractProcessSelected,
  );

  const tab = authorizedUiElementToContractTab(detailUiElement);
  tab.state = convertToTabViewsState(tab.state, "tabs");

  contractServiceProvider.addContractTab(tab);
}

onNewContractProcessSelected.$inject = [
  ProcessInformationServiceName,
  "$state",
  "contract",
];
export function onNewContractProcessSelected(
  processInformationService: ProcessInformationService,
  $state: StateService,
  contract: Contract,
) {
  return [
    "process",
    (process: ProcessInformation) =>
      processInformationService
        .createProcess(process, {
          ContractInstanceId: contract.ContractInstanceId,
        })
        .then((processResponse) =>
          $state.go(".detail", {
            processInstancesId: processResponse.Id,
          }),
        ),
  ];
}

function getDefaultQueryForContractInstance(contract: Contract) {
  return { ContractInstanceId: contract.ContractInstanceId };
}

function authorizedUiElementToContractTab(
  authorizedUiElement: AuthorizedUiElementConfig,
): IContractTab {
  return {
    name: authorizedUiElement.displayName,
    state: authorizedUiElement.stateDefinition!,
    uiSref: authorizedUiElement.uiSrefFragment!,
    children:
      authorizedUiElement.children?.map((c) =>
        authorizedUiElementToContractTab(c),
      ) ?? [],
  };
}

function convertToTabViewsState(
  stateDefinition: Ng1StateDeclaration,
  viewTarget: string = "",
): Ng1StateDeclaration {
  const url = stateDefinition.url;
  const resolve = stateDefinition.resolve;
  delete stateDefinition.url;
  delete stateDefinition.resolve;

  return {
    url,
    resolve,
    views: {
      [viewTarget]: stateDefinition,
    },
  };
}

function getMachineProcessGroupConfig(
  processDetailTemplate: string,
): ProcessGroupConfig {
  return {
    getCreateProcessDto: [
      "dataObject",
      (dataObject) => {
        return { MachineId: dataObject.MachineId };
      },
    ],
    onSelectDataObject: openSelectMachineModal,
    processGroupId: "machine",
    processDetailTemplate,
    additionalGridColumns: [
      RecordGridViewServiceName,
      (recordGridViewService: RecordGridViewService) => {
        return (
          recordGridViewService.getGridConfiguration(contractObjectName)
            ?.columns ?? []
        );
      },
    ],
  };
}

function getContractProcessGroupConfig(
  processDetailTemplate: string,
): ProcessGroupConfig {
  return {
    getCreateProcessDto: [
      "dataObject",
      (dataObject) => {
        return { ContractInstanceId: dataObject.ContractInstanceId };
      },
    ],
    onSelectDataObject: openSelectContractModal,
    processGroupId: "contract",
    processDetailTemplate,
    additionalGridColumns: [
      RecordGridViewServiceName,
      (recordGridViewService: RecordGridViewService) => {
        return (
          recordGridViewService.getGridConfiguration(contractObjectName)
            ?.columns ?? []
        );
      },
    ],
  };
}

openSelectContractModal.$inject = [
  "$q",
  SelectContractModalServiceName,
  RecordGridViewServiceName,
  ColumnDefinitionServiceName,
];
function openSelectContractModal(
  $q: IQService,
  selectContractModalService: SelectContractModalService,
  recordGridViewService: RecordGridViewService,
  columnDefinitionService: ColumnDefinitionService,
) {
  const gridConfiguration =
    recordGridViewService.getGridConfiguration(contractObjectName)!;

  gridConfiguration.columns = [
    columnDefinitionService.getCommandColDef({
      headerName: "select",
      iconClass: "fa fa-plus-circle text-primary",
      name: "select",
    }),
    ...(gridConfiguration.columns ?? []),
  ];
  gridConfiguration.onItemCommand = (args: IRowCommandEventArgs<Record>) => {
    const { commandName, data } = args;
    if (commandName === "select") {
      modalInstance.close(data);
    }
    return $q.when();
  };

  const modalInstance = selectContractModalService.openModal({
    gridConfiguration,
  });
  return modalInstance.result.then((r) => <Record>r);
}

openSelectMachineModal.$inject = [
  "$q",
  SelectRecordModalServiceName,
  RecordGridViewServiceName,
  ColumnDefinitionServiceName,
];
function openSelectMachineModal(
  $q: IQService,
  selectRecordModalService: SelectRecordModalService,
  recordGridViewService: RecordGridViewService,
  columnDefinitionService: ColumnDefinitionService,
) {
  const gridConfiguration =
    recordGridViewService.getGridConfiguration(machineObjectName)!;

  // gridConfiguration.columns = [
  //   columnDefinitionService.getCommandColDef({
  //     headerName: "select",
  //     iconClass: "fa fa-plus-circle text-primary",
  //     name: "select",
  //   }),
  //   ...(gridConfiguration.columns ?? []),
  // ];
  // gridConfiguration.onItemCommand = (args: IRowCommandEventArgs<Record>) => {
  //   const { commandName, data } = args;
  //   if (commandName === "select") {
  //     modalInstance.close(data);
  //   }
  //   return $q.when();
  // };

  const modalInstance = selectRecordModalService.openModal({
    onItemSelected: (args) => {
      modalInstance.close(args);
    },
    title: "Select Machine",
    gridConfiguration,
  });
  return modalInstance.result.then((r) => <Record>r);
}
